import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Check, Sun, Thermometer, BatteryCharging, Droplets, Cpu, Zap } from 'lucide-react';
import { SOLUTIONS } from '../mock';
import PartnerBanner from '../components/PartnerBanner';
import CTASection from '../components/CTASection';

const ICONS = { Sun, Thermometer, BatteryCharging, Droplets, Cpu, Zap };

export default function SolutionDetail() {
  const { slug } = useParams();
  const sol = SOLUTIONS.find(s => s.slug === slug);
  if (!sol) return <Navigate to="/solutions" replace />;
  const Icon = ICONS[sol.icon] || Sun;
  const others = SOLUTIONS.filter(s => s.slug !== slug).slice(0,3);

  return (
    <div>
      <section className="pt-36 pb-12 bg-grad-hero">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Link to="/solutions" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-[#125082] mb-7"><ArrowLeft className="w-4 h-4" /> Toutes les solutions</Link>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="w-12 h-12 rounded-full bg-[#80b23f]/15 flex items-center justify-center"><Icon className="w-6 h-6 text-[#80b23f]" /></span>
                <span className="text-[10px] tracking-[0.18em] uppercase text-[#125082] font-semibold">{sol.eyebrow}</span>
              </div>
              <h1 className="font-display text-5xl md:text-6xl text-gray-900 leading-[1.05]">{sol.title}</h1>
              <p className="mt-6 text-gray-600 leading-relaxed">{sol.description}</p>
              <div className="mt-9 flex flex-wrap gap-4">
                <Link to="/contact" className="btn-primary inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-medium">Demander un devis <ArrowRight className="w-4 h-4" /></Link>
                <Link to="/solutions" className="btn-outline inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-medium bg-white">Autres solutions</Link>
              </div>
            </div>
            <div className="rounded-3xl overflow-hidden aspect-[4/3] shadow-xl">
              <img src={sol.image} alt={sol.title} className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <p className="eyebrow">Les avantages</p>
          <h2 className="font-display text-4xl md:text-5xl mt-4 text-gray-900 max-w-2xl">Ce que vous gagnez avec <span className="italic-accent">cette solution.</span></h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {sol.benefits.map((b,i) => (
              <div key={i} className="flex items-start gap-4 p-6 bg-[#f1f1f1] rounded-2xl">
                <span className="w-9 h-9 rounded-full bg-[#80b23f] text-white flex items-center justify-center shrink-0"><Check className="w-4 h-4" /></span>
                <span className="font-medium text-gray-900 pt-1.5">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PartnerBanner />

      <section className="py-20 bg-[#f1f1f1]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <p className="eyebrow">Autres solutions</p>
          <h2 className="font-display text-4xl mt-4 text-gray-900 mb-10">Complétez votre <span className="italic-accent">écosystème énergétique.</span></h2>
          <div className="grid md:grid-cols-3 gap-6">
            {others.map(s => (
              <Link to={`/solutions/${s.slug}`} key={s.slug} className="solution-card group bg-white rounded-2xl overflow-hidden border border-gray-200">
                <div className="aspect-[4/3] overflow-hidden"><img src={s.image} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
                <div className="p-6">
                  <h3 className="font-display text-xl text-gray-900 mb-2">{s.title}</h3>
                  <span className="text-sm font-medium text-[#125082] inline-flex items-center gap-1 group-hover:gap-2 transition-all">En savoir plus <ArrowRight className="w-4 h-4" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
